import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";
import { AnalyticsCharts } from "../components/admin/AnalyticsCharts";
import { ClientTable } from "../components/admin/ClientTable";
import { ClientDrawer } from "../components/admin/ClientDrawer";

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const limit = 10;

  const loadCore = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/revenue-series"),
      ]);
      setStats(s.data);
      setSeries(r.data.series);
    } catch (e) {
      console.error("Admin core load failed:", e);
      setErr(formatApiError(e));
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/clients?skip=${skip}&limit=${limit}&q=${encodeURIComponent(q)}`);
      setClients(data.clients);
      setTotal(data.total);
    } catch (e) {
      console.error("Admin clients load failed:", e);
    }
  }, [skip, q]);

  useEffect(() => {
    if (user === null) { nav("/login"); return; }
    if (user && user.role !== "admin") { nav("/feed"); return; }
    if (user) loadCore();
  }, [user, nav, loadCore]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const t = setTimeout(loadClients, 300);
    return () => clearTimeout(t);
  }, [user, loadClients]);

  if (!user || user.role !== "admin") return null;

  const openClient = async (id) => {
    setDrawerOpen(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/clients/${id}`);
      setDetail(data);
    } catch (e) {
      console.error("Client detail load failed:", e);
    }
  };

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/clients/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "drcuts-customers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export failed:", e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-32 pb-24 px-6">
      <Seo title="Command Center" description="Dr. Cuts admin command center." path="/admin" noindex />
      <div className="max-w-[1400px] mx-auto">
        <div className="overline">Command center</div>
        <h1 className="font-display text-5xl sm:text-6xl mt-3">Business at a glance.</h1>
        <p className="text-white/60 mt-2">Revenue, members and pending swings — live.</p>

        {err && <div className="text-red-400 mt-6" data-testid="admin-error">{err}</div>}

        <AnalyticsCharts stats={stats} series={series} />

        <ClientTable
          clients={clients} total={total} skip={skip} limit={limit}
          q={q} setQ={setQ} setSkip={setSkip}
          onOpen={openClient} onExport={downloadCsv} exporting={exporting}
        />
      </div>

      <ClientDrawer open={drawerOpen} onOpenChange={setDrawerOpen} detail={detail} />
    </div>
  );
}
